import Logger from '@utils/log'
import { useState, useEffect, useCallback } from 'react'
import { useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import useHotkeys from '@hook/useHotkeys'
import cn from 'clsx'

import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md'

import './DocumentViewerSearchResultPager.css'

const log = new Logger('c/collection/DocumentViewerSearchResultPager')

const DocumentViewerSearchResultPager = ({
    className,
    loading,
    pageMatches,
    setPageIndex,
    prevPageWithMatchesIndex,
    nextPageWithMatchesIndex,
}) => {
    
    //region State
    
    /**
     * Query to highlight within the document
     * Initially is the documents search query, but if the user
     * changes it, the highlights become independent of the document search
     * 
     * @type {String}
     */
    const [query, setQuery] = useState('')
    
    const documentSearchQuery = useWireValue(store.documentSearchQuery)
    
    //endregion State
    
    //region Methods
    
    /**
     * Changes to the previous page with search results, if exists
     */
    const changePagePrev = useCallback(() => {
        
        if (loading) return
        
        if (prevPageWithMatchesIndex === null)
            return log.w('called changePagePrev but no prev page available')
        
        setPageIndex(prevPageWithMatchesIndex)
        
    }, [loading, prevPageWithMatchesIndex, setPageIndex])
    
    /**
     * Changes to the next page with search results, if exists
     */
    const changePageNext = useCallback(() => {
        
        if (loading) return
        
        if (!nextPageWithMatchesIndex)
            return log.w('called changePageNext but no next page available')
        
        setPageIndex(nextPageWithMatchesIndex)
        
    }, [loading, nextPageWithMatchesIndex, setPageIndex])
    
    //endregion Methods
    
    //region Hooks
    
    useEffect(() => {
        
        // Set the query to the one from the URL, if exists
        setQuery(prev => prev === '' ? documentSearchQuery : prev)
        
    }, [documentSearchQuery])
    
    useHotkeys([
        {
            key: 'F3',
            handler: changePageNext,
        },
        {
            key: 'F3',
            modifier: 'shiftKey',
            handler: changePagePrev,
        },
    ])
    
    //endregion Hooks
    
    // @todo disable prev/next btns depending if has matches
    
    return (
        
        <div className={cn(
            'DocumentSearchResultPager',
            'flex justify-end items-center content-center gap-6',
            className,
        )}>
            
            <span className="text-base cursor-help">
                {pageMatches?.counts?.current ?? '0'}
                {' '}
                <abbr
                    className="tooltip tooltip-primary tooltip-bottom"
                    data-tip={`of ${pageMatches?.counts?.all ?? 0} total matches for this document`}>
                    results on page
                </abbr>
                {' '}
                for
            </span>
            
            <mark
                className="block px-3 py-1 rounded-sm text-base highlight"
                title={`Search results for "${query}"`}>
                &quot;{query}&quot;
            </mark>
            
            <div className="flex justify-end items-center content-center gap-2">
                
                <button
                    className={cn('btn-match-nav btn btn-sm btn-ghost btn-square', {
                        'cursor-wait': loading,
                    })}
                    data-tip="Previous Result (Shift+F3)"
                    onClick={changePagePrev}>
                    <MdKeyboardArrowLeft className="text-3xl" />
                </button>
                
                <button
                    className={cn('btn-match-nav btn btn-sm btn-ghost btn-square', {
                        'cursor-wait': loading,
                    })}
                    data-tip="Next Result (F3)"
                    onClick={changePageNext}>
                    <MdKeyboardArrowRight className="text-3xl" />
                </button>
            
            </div>
        
        </div>
        
    )
    
}

export default DocumentViewerSearchResultPager
